/*
 * Milyano: auto-loads the next page of a paginated product/blog/collection
 * listing as the shopper scrolls near the end, instead of requiring a click
 * on the numbered page links. Opt-in per call site via the `results_selector`
 * param on the shared `pagination` snippet -- see snippets/pagination.liquid.
 *
 * Follows the same IntersectionObserver + Section Rendering API pattern
 * already used by the theme's own `product-recommendations` element
 * (assets/global.js), so it fetches a plain HTML re-render of the current
 * section and lifts out just the piece it needs.
 */
if (!customElements.get('infinite-scroll-pagination')) {
  customElements.define(
    'infinite-scroll-pagination',
    class InfiniteScrollPagination extends HTMLElement {
      constructor() {
        super();
        this.observer = undefined;
      }

      connectedCallback() {
        this.nextUrl = this.dataset.nextUrl;
        this.resultsSelector = this.dataset.resultsSelector;
        this.sectionId = this.dataset.sectionId;
        this.list = this.querySelector('.pagination__list');
        this.status = this.querySelector('.pagination__auto-load-status');

        if (!this.nextUrl) return;

        // Progressive enhancement: hide the numbered page links while JS is
        // driving the auto-load. If a fetch ever fails, they're restored so
        // the shopper always has a manual way to see more products.
        if (this.list) this.list.style.display = 'none';

        this.observer = new IntersectionObserver(
          (entries, observer) => {
            if (!entries[0].isIntersecting) return;
            observer.unobserve(this);
            this.loadNextPage();
          },
          { rootMargin: '0px 0px 600px 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.unobserve(this);
      }

      loadNextPage() {
        if (this.status) this.status.hidden = false;

        const separator = this.nextUrl.includes('?') ? '&' : '?';
        const url = `${this.nextUrl}${separator}section_id=${this.sectionId}`;

        fetch(url)
          .then((response) => {
            if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
            return response.text();
          })
          .then((text) => {
            const html = new DOMParser().parseFromString(text, 'text/html');

            const newResults = html.querySelector(this.resultsSelector);
            const liveResults = document.querySelector(this.resultsSelector);
            if (newResults && liveResults) {
              Array.from(newResults.children).forEach((child) => {
                liveResults.appendChild(child);
              });
            }

            const newPagination = html.querySelector('infinite-scroll-pagination');
            if (newPagination) {
              // Swapping in the freshly-fetched pagination element carries
              // over its own data-next-url, so this same custom element
              // continues the chain on its own connectedCallback -- or
              // quietly does nothing further once there is no next page.
              this.replaceWith(newPagination);
            } else {
              this.remove();
            }
          })
          .catch((e) => {
            console.error(e);
            if (this.list) this.list.style.display = '';
            if (this.status) this.status.hidden = true;
          });
      }
    }
  );
}
